/**
 * Where transport is measured to.
 *
 * By default the transport raster is the weighted mean journey time to the
 * busiest destinations, which the manifest calls by one name ("Central
 * London"). A reader may swap that for any single place on the menu. Each is a
 * baked raster like a schools phase, but there are hundreds of them, so the
 * menu arrives as a small index and a raster is fetched when it is picked.
 * Only the last few are kept, so a reader trying place after place does not
 * pile up a quarter of a megabyte for each; going back further is a refetch,
 * which the browser's HTTP cache usually answers without the network.
 *
 * While the reader is choosing, every place on the menu is drawn on the map in
 * the transport colour, sized by how many people travel there, and tapping one
 * is the choice.
 */

import type { GeoJSONSource, Map as MapLibreMap, PointLike } from "maplibre-gl";

import type { Destination, LayerSpec } from "./manifest.ts";
import { loadArray, loadJson } from "./manifest.ts";

const SOURCE = "propmap-destinations";
const DOTS = `${SOURCE}-dots`;
const LABELS = `${SOURCE}-labels`;

/**
 * How many picked rasters stay in memory, most recent first. The default is
 * not one of them: `main.ts` holds it for the whole session.
 */
const KEEP = 3;

/** How far from a dot, in px, a tap still counts as a tap on it. */
const TAP_PX = 12;

export class Destinations {
  readonly layer: LayerSpec;
  private menu: Promise<Destination[]> | null = null;
  private readonly bytes = new Map<string, Promise<Uint8Array>>();

  constructor(layer: LayerSpec) {
    this.layer = layer;
  }

  /** What the layer measures to until a reader picks somewhere. */
  get defaultLabel(): string {
    return this.layer.destinations!.default_label;
  }

  list(): Promise<Destination[]> {
    this.menu ??= loadJson<Destination[]>(this.layer.destinations!.index);
    return this.menu;
  }

  async find(key: string): Promise<Destination | null> {
    return (await this.list()).find((d) => d.key === key) ?? null;
  }

  /** A destination's raster, from the last `KEEP` picked or fetched afresh. */
  raster(place: Destination): Promise<Uint8Array> {
    let pending = this.bytes.get(place.key);
    if (pending) {
      // re-inserted, so the Map's order stays least recently used first
      this.bytes.delete(place.key);
    } else {
      pending = loadArray(place.asset) as Promise<Uint8Array>;
      // a failed fetch is not remembered, so picking the place again retries it
      pending.catch(() => this.bytes.delete(place.key));
    }
    this.bytes.set(place.key, pending);
    while (this.bytes.size > KEEP) this.bytes.delete(this.bytes.keys().next().value!);
    return pending;
  }
}

/** The menu's dots and names, hidden until the picker opens. */
export function addDestinationLayers(map: MapLibreMap, colour: string): void {
  map.addSource(SOURCE, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  map.addLayer({
    id: DOTS,
    type: "circle",
    source: SOURCE,
    layout: { visibility: "none" },
    paint: {
      "circle-color": colour,
      "circle-opacity": 0.9,
      "circle-stroke-color": ["case", ["get", "on"], "#1c1b18", "#ffffff"] as never,
      "circle-stroke-width": ["case", ["get", "on"], 3, 1.5] as never,
      "circle-radius": ["+", ["case", ["get", "on"], 3, 0],
        ["interpolate", ["linear"], ["sqrt", ["get", "share"]], 0, 4, 0.1, 7, 0.25, 11]] as never,
    },
  });
  map.addLayer({
    id: LABELS,
    type: "symbol",
    source: SOURCE,
    layout: {
      visibility: "none",
      "text-field": ["get", "label"],
      "text-font": ["Noto Sans Regular"],
      "text-size": 11.5,
      "text-offset": [0, 1.1],
      "text-anchor": "top",
      "text-optional": true,
      // the busiest name wins where two collide
      "symbol-sort-key": ["-", 0, ["get", "share"]] as never,
    },
    paint: {
      "text-color": "#1c1b18",
      "text-halo-color": "#ffffff",
      "text-halo-width": 1.4,
    },
  });
}

/** Show the menu on the map with `current` ringed, or hide it (`places` null). */
export function showDestinations(map: MapLibreMap, places: Destination[] | null,
                                 current: string | null): void {
  const source = map.getSource(SOURCE) as GeoJSONSource | undefined;
  source?.setData({
    type: "FeatureCollection",
    features: (places ?? []).map((d) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [d.lon, d.lat] },
      properties: { key: d.key, label: d.label, share: d.share, on: d.key === current },
    })),
  });
  const visibility = places ? "visible" : "none";
  for (const id of [DOTS, LABELS]) map.setLayoutProperty(id, "visibility", visibility);
}

/** The key of the menu dot nearest a tap, within a finger's width. */
export function destinationAt(map: MapLibreMap, point: { x: number; y: number }): string | null {
  const box: [PointLike, PointLike] = [[point.x - TAP_PX, point.y - TAP_PX],
                                       [point.x + TAP_PX, point.y + TAP_PX]];
  let best: { key: string; d: number } | null = null;
  for (const feature of map.queryRenderedFeatures(box, { layers: [DOTS] })) {
    const [lon, lat] = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
    const at = map.project([lon, lat]);
    const d = Math.hypot(at.x - point.x, at.y - point.y);
    if (!best || d < best.d) best = { key: String(feature.properties?.["key"]), d };
  }
  return best?.key ?? null;
}

/** A pointer over a menu dot, while the picker is open. */
export function hoverCursor(map: MapLibreMap): void {
  map.on("mouseenter", DOTS, () => { map.getCanvas().style.cursor = "pointer"; });
  map.on("mouseleave", DOTS, () => { map.getCanvas().style.cursor = ""; });
}
